import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { useChildRestrictions, useUpdateRestrictions } from '../hooks/useParental'
import { TimeWindowsEditor } from './TimeWindowsEditor'
import {
  DAILY_DURATIONS,
  SESSION_DURATIONS,
  type Restrictions,
  type RestrictionsPayload,
  type ToneLevel,
} from '../types'

interface RestrictionsFormProps {
  readonly childId: string
}

/** Ce que le serveur renvoie, débarrassé des listes de référence. */
function toPayload(saved: Restrictions): RestrictionsPayload {
  return {
    time_windows: saved.time_windows,
    max_session_minutes: saved.max_session_minutes,
    max_daily_minutes: saved.max_daily_minutes,
    tone_level: saved.tone_level,
    restricted_topics: saved.restricted_topics,
    alerts: saved.alerts,
  }
}

const FIELD =
  'w-full rounded-lg border border-surface-200 px-3 py-2 text-sm text-neutral-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'

export function RestrictionsForm({ childId }: RestrictionsFormProps) {
  const { t } = useTranslation()
  const { data: saved, isLoading } = useChildRestrictions(childId)
  const update = useUpdateRestrictions(childId)
  // Tant que le parent n'a rien touché, le formulaire affiche ce que le serveur
  // a renvoyé : la valeur est dérivée au rendu plutôt que recopiée dans un effet.
  const [edited, setEdited] = useState<RestrictionsPayload | null>(null)

  if (isLoading || !saved) {
    return <div className="h-64 animate-pulse rounded-2xl bg-surface-container" />
  }

  const draft = edited ?? toPayload(saved)
  const setDraft = setEdited

  function toggle(list: string[], value: string): string[] {
    return list.includes(value) ? list.filter((item) => item !== value) : [...list, value]
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    update.mutate(draft)
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5">
      <h2 className="font-display text-lg font-semibold text-on-surface">
        {t('parent.restrictions.title')}
      </h2>

      {update.isSuccess && <Alert variant="success">{t('parent.restrictions.saved')}</Alert>}
      {update.isError && <Alert variant="error">{t('parent.restrictions.error')}</Alert>}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <span className="flex flex-col gap-1">
          <label htmlFor="max-session" className="text-sm font-medium text-surface-800">
            {t('parent.restrictions.session_limit')}
          </label>
          <select
            id="max-session"
            value={draft.max_session_minutes ?? ''}
            onChange={(event) =>
              setDraft({
                ...draft,
                max_session_minutes: event.target.value === '' ? null : Number(event.target.value),
              })
            }
            className={FIELD}
          >
            <option value="">{t('parent.restrictions.unlimited')}</option>
            {SESSION_DURATIONS.map((minutes) => (
              <option key={minutes} value={minutes}>
                {t('parent.restrictions.minutes', { count: minutes })}
              </option>
            ))}
          </select>
        </span>

        <span className="flex flex-col gap-1">
          <label htmlFor="max-daily" className="text-sm font-medium text-surface-800">
            {t('parent.restrictions.daily_limit')}
          </label>
          <select
            id="max-daily"
            value={draft.max_daily_minutes ?? ''}
            onChange={(event) =>
              setDraft({
                ...draft,
                max_daily_minutes: event.target.value === '' ? null : Number(event.target.value),
              })
            }
            className={FIELD}
          >
            <option value="">{t('parent.restrictions.unlimited')}</option>
            {DAILY_DURATIONS.map((minutes) => (
              <option key={minutes} value={minutes}>
                {t('parent.restrictions.minutes', { count: minutes })}
              </option>
            ))}
          </select>
        </span>
      </div>

      <span className="flex flex-col gap-1">
        <label htmlFor="tone-level" className="text-sm font-medium text-surface-800">
          {t('parent.restrictions.tone')}
        </label>
        <select
          id="tone-level"
          value={draft.tone_level}
          onChange={(event) => setDraft({ ...draft, tone_level: event.target.value as ToneLevel })}
          className={FIELD}
        >
          {(['all', 'no_informal', 'formal_only'] as const).map((level) => (
            <option key={level} value={level}>
              {t(`parent.restrictions.tones.${level}`)}
            </option>
          ))}
        </select>
      </span>

      <TimeWindowsEditor
        value={draft.time_windows}
        onChange={(time_windows) => setDraft({ ...draft, time_windows })}
      />

      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-sm font-medium text-surface-800">
          {t('parent.restrictions.topics')}
        </legend>
        {saved.available_topics.map((topic) => (
          <label key={topic} className="flex items-center gap-2 text-sm text-on-surface">
            <input
              type="checkbox"
              checked={draft.restricted_topics.includes(topic)}
              onChange={() =>
                setDraft({ ...draft, restricted_topics: toggle(draft.restricted_topics, topic) })
              }
              className="h-4 w-4 accent-primary"
            />
            {t(`parent.restrictions.topic_names.${topic}`)}
          </label>
        ))}
      </fieldset>

      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-sm font-medium text-surface-800">
          {t('parent.restrictions.alerts')}
        </legend>
        {saved.available_alerts.map((alert) => (
          <label key={alert} className="flex items-center gap-2 text-sm text-on-surface">
            <input
              type="checkbox"
              checked={draft.alerts[alert] ?? true}
              onChange={() =>
                setDraft({ ...draft, alerts: { ...draft.alerts, [alert]: !(draft.alerts[alert] ?? true) } })
              }
              className="h-4 w-4 accent-primary"
            />
            {t(`parent.restrictions.alert_names.${alert}`)}
          </label>
        ))}
      </fieldset>

      <div>
        <Button type="submit" isLoading={update.isPending}>
          {t('parent.restrictions.save')}
        </Button>
      </div>
    </form>
  )
}
